import React from 'react';
import { Stack } from '@mui/material';
import { useTranslation } from '../../app/LanguageContext';
import { resolveProfitDisplayFromResult } from './budgetEngineV2';
import {
  clearSellingPriceOverride,
  syncSellingPriceFromProfitPercent,
  syncSellingPriceFromProfitTotal,
  syncSellingPriceFromProfitUnit,
} from './priceSimulatorMetadata';

const nf = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

export default function ProfitMarginFields({
  product,
  result,
  onProductChange,
  PercentFieldComponent,
  CurrencyFieldComponent,
}) {
  const { translate } = useTranslation();
  const quantity = Math.max(1, parseInt(product.quantity, 10) || 1);
  const priceAfterReinvest = nf(result?.profit_margin?.price_before);
  const disabled = !result || priceAfterReinvest <= 0;
  const { percent, amountTotal, amountUnit } = resolveProfitDisplayFromResult(result, quantity);

  const handlePercentChange = (v) => {
    if (disabled) return;
    const pct = Math.max(0, Math.round(nf(v)));
    if (pct <= 0) {
      onProductChange((p) => ({ ...p, ...clearSellingPriceOverride() }));
      return;
    }
    const synced = syncSellingPriceFromProfitPercent(pct, priceAfterReinvest, quantity);
    onProductChange((p) => ({ ...p, ...synced }));
  };

  const handleTotalChange = (v) => {
    if (disabled) return;
    const amount = nf(v);
    if (amount <= 0) {
      onProductChange((p) => ({ ...p, ...clearSellingPriceOverride() }));
      return;
    }
    const synced = syncSellingPriceFromProfitTotal(amount, priceAfterReinvest, quantity);
    onProductChange((p) => ({ ...p, ...synced }));
  };

  const handleUnitChange = (v) => {
    if (disabled) return;
    const unit = nf(v);
    if (unit <= 0) {
      onProductChange((p) => ({ ...p, ...clearSellingPriceOverride() }));
      return;
    }
    const synced = syncSellingPriceFromProfitUnit(unit, priceAfterReinvest, quantity);
    onProductChange((p) => ({ ...p, ...synced }));
  };

  const PercentField = PercentFieldComponent;
  const CurrencyField = CurrencyFieldComponent;

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
      <PercentField
        size="small"
        fullWidth
        disabled={disabled}
        label={translate('price_simulate.profit_margin_pct')}
        value={percent}
        onChange={handlePercentChange}
        sx={{ flex: 1, minWidth: 0 }}
      />
      <CurrencyField
        size="small"
        fullWidth
        disabled={disabled}
        label={translate('price_simulate.profit_margin_amount')}
        value={amountTotal}
        onChange={handleTotalChange}
        sx={{ flex: 1, minWidth: 0 }}
      />
      <CurrencyField
        size="small"
        fullWidth
        disabled={disabled}
        label={translate('price_simulate.profit_margin_amount_unit')}
        value={amountUnit}
        onChange={handleUnitChange}
        sx={{ flex: 1, minWidth: 0 }}
      />
    </Stack>
  );
}
